import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { firstValueFrom, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { SentryLoggerService } from './sentry-logger.service';

@Injectable({
  providedIn: 'root',
})
export class CheckUpdatesService {
  private swUpdate: SwUpdate = inject(SwUpdate);
  private sentryLogger: SentryLoggerService = inject(SentryLoggerService);
  private platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  async checkForUpdates(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }

    if (this.swUpdate.isEnabled) {
      try {
        await this.waitForServiceWorkerReady();

        const updateAvailable = await firstValueFrom(
          this.swUpdate.versionUpdates.pipe(map(() => from(this.swUpdate.checkForUpdate()))),
        );

        if (updateAvailable) {
          console.log(
            '%cNew version available, clearing caches and reloading...',
            'color: lightgreen; font-weight: bold;',
          );
          await this.clearAllCaches();
          await this.swUpdate.activateUpdate();
          window.location.reload();
        }
      } catch (error) {
        console.error('Error checking for updates:', error);
        this.sentryLogger.captureException(error, {
          component: 'CheckUpdatesService',
          action: 'checkForUpdates',
          extra: {
            swUpdateEnabled: this.swUpdate.isEnabled,
          },
        });
      }
    } else {
      console.log('Service Worker is not enabled.');
    }
  }

  private async waitForServiceWorkerReady(): Promise<void> {
    if (!this.isBrowser) {
      return Promise.resolve();
    }

    try {
      return (await navigator.serviceWorker?.ready) ? Promise.resolve() : Promise.reject();
    } catch (error) {
      this.sentryLogger.captureException(error, {
        component: 'CheckUpdatesService',
        action: 'waitForServiceWorkerReady',
      });
      throw error;
    }
  }

  private async clearAllCaches(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }

    if ('caches' in window) {
      try {
        const cacheNames = await window.caches.keys();

        if (cacheNames.length === 0) {
          console.log('No caches to delete');
          return;
        }

        const cacheDeletePromises = cacheNames.map((cacheName) => {
          console.log(`Deleting cache: ${ cacheName }`);
          return window.caches.delete(cacheName);
        });

        await Promise.all(cacheDeletePromises);
        console.log('All caches deleted successfully');
      } catch (error) {
        console.error('Error clearing caches:', error);
        this.sentryLogger.captureException(error, {
          component: 'CheckUpdatesService',
          action: 'clearAllCaches',
          extra: {
            cacheApiSupported: 'caches' in window,
          },
        });
      }
    } else {
      console.warn('Cache API not supported in this browser');
      this.sentryLogger.captureMessage('Cache API not supported in browser', 'warning', {
        component: 'CheckUpdatesService',
        action: 'clearAllCaches',
      });
    }
  }
}
